// Resolve front/back preview image URLs from a line item's customAttributes.
//
// Shopify customizer apps don't agree on a key name for the rendered preview:
//
//   1. Soccer Post Team Portal — `Preview front` / `Preview back`
//   2. PPLR (and other 3rd-party customizers) — a single `Preview` URL,
//      sometimes with a `_pplr_preview` marker pointing at the attr key
//
// The portals (printout, picking, pressing, packing) used to look up only
// `preview front`, so PPLR-style items fell back to the raw Shopify product
// image and the operator never saw the customer's custom design. This helper
// covers both conventions in one place so call sites can ask "what should I
// show as the customized image?" and not care which app produced it.

const isUrl = (v) => typeof v === 'string' && /^https?:\/\//.test(v);

function find(attrs, key) {
    const target = (key || '').toLowerCase();
    return attrs.find(a => (a?.key || '').toLowerCase() === target)?.value || null;
}

export function getPreviewImageUrls(attrs = []) {
    if (!Array.isArray(attrs)) return { front: null, back: null };

    // Convention 1: Soccer Post Team Portal — separate front/back URLs.
    const front = find(attrs, 'Preview front');
    const back  = find(attrs, 'Preview back');
    if (isUrl(front) || isUrl(back)) {
        return { front: isUrl(front) ? front : null, back: isUrl(back) ? back : null };
    }

    // Convention 2: PPLR-style customizer — `_pplr_preview` names the attr
    // that holds the URL (typically just `Preview`). Use that as the front.
    const pplrKey = find(attrs, '_pplr_preview');
    if (pplrKey) {
        const url = find(attrs, pplrKey);
        if (isUrl(url)) return { front: url, back: null };
    }

    // Convention 2b: bare `Preview` attribute with a URL value.
    const bare = find(attrs, 'Preview');
    if (isUrl(bare)) return { front: bare, back: null };

    return { front: null, back: null };
}

// Convenience: just the front URL when callers don't render a back image.
export function getPreviewFrontUrl(attrs = []) {
    return getPreviewImageUrls(attrs).front;
}

// Rewrite an image URL to request a downscaled variant. Bulk print of ~50
// orders used to load multi-MB originals for every thumbnail.
//
// Strategy by host:
//   - cdn.shopify.com → use Shopify's native `_WIDTHx` URL transform
//     (no server work, CDN serves pre-scaled directly).
//   - soccer-post-bucket.s3.amazonaws.com, d3dkz69a8h9zhh.cloudfront.net
//     → route through `/api/image-proxy` which Pillow-resizes + disk-caches.
//   - Anything else → leave unchanged.
//
// Usage: `shopifyResize(url, 400)` → narrows to <=400px width.
const _PROXY_HOSTS = ['soccer-post-bucket.s3.amazonaws.com', 'd3dkz69a8h9zhh.cloudfront.net'];

export function shopifyResize(url, width) {
    if (!url || typeof url !== 'string' || !Number.isFinite(width)) return url;
    if (url.includes('cdn.shopify.com')) {
        // Insert _WIDTHx before extension. Replace any existing _NxN suffix
        // to avoid double-resizing. Query (?v=...) preserved.
        return url.replace(
            /(_\d+x\d*)?(\.[a-zA-Z0-9]+)(\?|$)/,
            `_${width}x$2$3`
        );
    }
    if (_PROXY_HOSTS.some(h => url.includes(h))) {
        return `/api/image-proxy?src=${encodeURIComponent(url)}&w=${width}`;
    }
    return url;
}
